import * as React from 'react';

import { cn } from '../lib/utils';

export interface SliderProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step'
  > {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  /** Every movement (drag / arrow key) — update local state here. */
  onValueChange?: (value: number) => void;
  /** Once the value settles (pointer released / key change) — persist here. */
  onValueCommit?: (value: number) => void;
}

/**
 * Single-thumb range slider — shadcn/scio3 `Slider` look (h-2 muted track, primary fill, h-5 w-5
 * primary thumb) on a native `<input type="range">`, because `@radix-ui/react-slider` is not a
 * dependency here. A native range input already carries role="slider", aria-value*, and arrow /
 * Home / End / PageUp keys. Change vs commit mirror Radix: the DOM `input` event (React's
 * onChange) → `onValueChange`; the DOM `change` event, which fires on release, → `onValueCommit`.
 */
const Slider = React.forwardRef<HTMLInputElement, SliderProps>(
  (
    { className, value, min = 0, max = 100, step = 1, onValueChange, onValueCommit, style, ...props },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const commitRef = React.useRef(onValueCommit);
    React.useEffect(() => {
      commitRef.current = onValueCommit;
    });

    React.useEffect(() => {
      const el = inputRef.current;
      if (!el) return;
      const onCommit = () => commitRef.current?.(Number(el.value));
      el.addEventListener('change', onCommit);
      return () => el.removeEventListener('change', onCommit);
    }, []);

    const pct = max > min ? ((Math.min(max, Math.max(min, value)) - min) / (max - min)) * 100 : 0;
    return (
      <input
        type="range"
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onValueChange?.(Number(e.target.value))}
        className={cn(
          'relative block h-2 w-full cursor-pointer touch-none select-none appearance-none rounded-full bg-transparent focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          '[&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full',
          '[&::-webkit-slider-thumb]:-mt-1.5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md',
          '[&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full',
          '[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:shadow-md',
          'focus-visible:[&::-webkit-slider-thumb]:ring-2 focus-visible:[&::-webkit-slider-thumb]:ring-ring focus-visible:[&::-webkit-slider-thumb]:ring-offset-2',
          className,
        )}
        style={{
          background: `linear-gradient(to right, var(--color-primary) ${pct}%, color-mix(in srgb, var(--color-muted-foreground) 20%, transparent) ${pct}%)`,
          ...style,
        }}
        {...props}
      />
    );
  },
);
Slider.displayName = 'Slider';

export { Slider };
